import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LogOut, CheckCircle2, XCircle, Trash2, RefreshCcw, CalendarDays, QrCode, Star, Plus, Edit3, Eye, EyeOff, ShieldCheck } from "lucide-react";
import CalendarView from "@/pages/CalendarView";
import { QRCodeCanvas } from "qrcode.react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gd_admin_token";

function authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewDraft, setReviewDraft] = useState(null); // null | { ...fields }
  const [tab, setTab] = useState("calendar");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, m, s, r] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers: authHeaders() }),
        axios.get(`${API}/admin/messages`, { headers: authHeaders() }),
        axios.get(`${API}/services`),
        axios.get(`${API}/admin/reviews`, { headers: authHeaders() }),
      ]);
      setBookings(b.data.bookings);
      setMessages(m.data.messages);
      setServices(s.data.services);
      setReviews(r.data.reviews);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        toast.error("Errore di caricamento");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/login`, creds);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Accesso effettuato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login fallito");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    navigate("/");
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${id}`, { status }, { headers: authHeaders() });
      toast.success("Aggiornato");
      load();
    } catch {
      toast.error("Errore");
    }
  };

  const removeBooking = async (id) => {
    if (!window.confirm("Eliminare definitivamente?")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, { headers: authHeaders() });
      toast.success("Eliminato");
      load();
    } catch {
      toast.error("Errore");
    }
  };

  // --- Reviews CRUD ---
  const emptyReview = {
    author: "",
    rating: 5,
    text: "",
    service_type: "",
    language: "it",
    verified: true,
    published: true,
  };
  const saveReview = async () => {
    if (!reviewDraft) return;
    const { id, ...body } = reviewDraft;
    if (!body.author?.trim() || !body.text?.trim()) {
      toast.error("Autore e testo obbligatori");
      return;
    }
    try {
      if (id) {
        await axios.patch(`${API}/admin/reviews/${id}`, body, { headers: authHeaders() });
        toast.success("Recensione aggiornata");
      } else {
        await axios.post(`${API}/admin/reviews`, body, { headers: authHeaders() });
        toast.success("Recensione creata");
      }
      setReviewDraft(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    }
  };
  const togglePublish = async (r) => {
    try {
      await axios.patch(
        `${API}/admin/reviews/${r.id}`,
        { published: !r.published },
        { headers: authHeaders() }
      );
      load();
    } catch {
      toast.error("Errore");
    }
  };
  const removeReview = async (id) => {
    if (!window.confirm("Eliminare questa recensione?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { headers: authHeaders() });
      toast.success("Recensione eliminata");
      load();
    } catch {
      toast.error("Errore");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F9F7F3] p-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm bg-white rounded-2xl p-8 shadow-[0_20px_60px_rgba(0,0,0,0.08)] space-y-4"
        >
          <h1 className="font-display text-3xl brand-text text-center">Admin</h1>
          <div>
            <Label>Username</Label>
            <Input
              data-testid="admin-username-input"
              value={creds.username}
              onChange={(e) => setCreds({ ...creds, username: e.target.value })}
              required
            />
          </div>
          <div>
            <Label>Password</Label>
            <Input
              type="password"
              data-testid="admin-password-input"
              value={creds.password}
              onChange={(e) => setCreds({ ...creds, password: e.target.value })}
              required
            />
          </div>
          <Button
            type="submit"
            data-testid="admin-login-btn"
            className="brand-bg text-white rounded-full w-full"
          >
            Accedi
          </Button>
        </form>
      </div>
    );
  }

  const statusBadge = (s) => {
    const map = {
      confirmed: { color: "bg-green-100 text-green-700", label: "Confermata" },
      cancelled: { color: "bg-red-100 text-red-700", label: "Annullata" },
      pending_payment: { color: "bg-amber-100 text-amber-700", label: "Nuova" },
    };
    const m = map[s] || map.pending_payment;
    return <Badge className={`${m.color} hover:${m.color}`}>{m.label}</Badge>;
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F9F7F3]">
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div className="font-display text-2xl brand-text">Gallura Drive · Admin</div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={load} data-testid="admin-reload-btn">
            <RefreshCcw className="w-4 h-4 mr-1" /> Aggiorna
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="admin-reminders-btn"
            onClick={async () => {
              try {
                const r = await axios.post(`${API}/admin/reminders/run`, {}, { headers: authHeaders() });
                toast.success(`Promemoria inviati: ${r.data.sent}`);
              } catch {
                toast.error("Errore promemoria");
              }
            }}
          >
            🌅 Promemoria
          </Button>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-1" /> Esci
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6">
        <div className="flex gap-2 mb-6 flex-wrap">
          <Button
            variant={tab === "calendar" ? "default" : "outline"}
            onClick={() => setTab("calendar")}
            data-testid="admin-tab-calendar"
            className={tab === "calendar" ? "brand-bg text-white" : ""}
          >
            <CalendarDays className="w-4 h-4 mr-1" /> Calendario
          </Button>
          <Button
            variant={tab === "bookings" ? "default" : "outline"}
            onClick={() => setTab("bookings")}
            data-testid="admin-tab-bookings"
            className={tab === "bookings" ? "brand-bg text-white" : ""}
          >
            Prenotazioni ({bookings.length})
          </Button>
          <Button
            variant={tab === "messages" ? "default" : "outline"}
            onClick={() => setTab("messages")}
            data-testid="admin-tab-messages"
            className={tab === "messages" ? "brand-bg text-white" : ""}
          >
            Messaggi ({messages.length})
          </Button>
          <Button
            variant={tab === "qr" ? "default" : "outline"}
            onClick={() => setTab("qr")}
            data-testid="admin-tab-qr"
            className={tab === "qr" ? "brand-bg text-white" : ""}
          >
            <QrCode className="w-4 h-4 mr-1" /> QR
          </Button>
          <Button
            variant={tab === "reviews" ? "default" : "outline"}
            onClick={() => setTab("reviews")}
            data-testid="admin-tab-reviews"
            className={tab === "reviews" ? "brand-bg text-white" : ""}
          >
            <Star className="w-4 h-4 mr-1" /> Recensioni ({reviews.length})
          </Button>
        </div>

        {tab === "calendar" && (
          <CalendarView
            bookings={bookings}
            services={services}
            authHeaders={authHeaders}
            onChange={load}
          />
        )}

        {tab === "bookings" && (
          <div className="space-y-3">
            {loading && <div className="text-slate-500">Caricamento...</div>}
            {!loading && bookings.length === 0 && (
              <div className="text-slate-500 bg-white rounded-xl p-6 text-center">
                Nessuna prenotazione
              </div>
            )}
            {bookings.map((b) => (
              <div
                key={b.id}
                data-testid={`admin-booking-${b.id}`}
                className="bg-white rounded-xl p-5 border border-slate-200 grid md:grid-cols-[1fr_auto] gap-4"
              >
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-display text-lg brand-text">{b.service_name}</span>
                    {statusBadge(b.status)}
                    <span className="font-display text-base accent-text">
                      €{Number(b.price_eur).toFixed(0)}
                    </span>
                  </div>
                  <div className="text-sm text-slate-600 grid md:grid-cols-2 gap-x-6 gap-y-1">
                    <div>📅 {b.booking_date} · {b.pickup_time}</div>
                    <div>👥 {b.passengers} pax · 🧳 {b.luggage}</div>
                    <div>📍 {b.pickup_location}</div>
                    <div>🏁 {b.dropoff_location}</div>
                    <div>👤 {b.customer_name}</div>
                    <div>
                      📞 <a className="brand-text underline" href={`tel:${b.customer_phone}`}>{b.customer_phone}</a>
                    </div>
                    <div className="md:col-span-2">✉️ {b.customer_email}</div>
                    {b.flight_number && <div>✈️ Volo: {b.flight_number}</div>}
                    {b.notes && <div className="md:col-span-2">📝 {b.notes}</div>}
                  </div>
                </div>
                <div className="flex md:flex-col gap-2">
                  <Button
                    size="sm"
                    onClick={() => updateStatus(b.id, "confirmed")}
                    disabled={b.status === "confirmed"}
                    data-testid={`admin-confirm-${b.id}`}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Conferma
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateStatus(b.id, "cancelled")}
                    disabled={b.status === "cancelled"}
                    data-testid={`admin-cancel-${b.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-1" /> Annulla
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeBooking(b.id)}
                    data-testid={`admin-delete-${b.id}`}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "qr" && (
          <div data-testid="admin-qr-tab" className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 border border-slate-200 text-center">
              <h2 className="font-display text-xl brand-text">QR Sito Web</h2>
              <p className="text-xs text-slate-500 mt-1 mb-5">Apre il sito di prenotazione</p>
              <div className="inline-block p-4 bg-white border border-slate-200 rounded-xl">
                <QRCodeCanvas id="gallura-qr" value={window.location.origin} size={220} fgColor="#0A2463" bgColor="#FFFFFF" level="H" includeMargin={true} />
              </div>
              <Button
                data-testid="qr-download-btn"
                className="brand-bg text-white rounded-full mt-5"
                onClick={() => {
                  const url = document.getElementById("gallura-qr").toDataURL("image/png");
                  const link = document.createElement("a");
                  link.download = "gallura-drive-sito-qr.png";
                  link.href = url;
                  link.click();
                }}
              >
                📥 Scarica PNG
              </Button>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-slate-200 text-center">
              <h2 className="font-display text-xl brand-text">QR Contatto (vCard)</h2>
              <p className="text-xs text-slate-500 mt-1 mb-5">Salva il contatto nel telefono del cliente</p>
              <div className="inline-block p-4 bg-white border border-slate-200 rounded-xl">
                <QRCodeCanvas
                  id="gallura-vcard-qr"
                  value={`BEGIN:VCARD\nVERSION:3.0\nFN:Gallura Drive\nORG:Gallura Drive\nTEL;TYPE=CELL:+393470011246\nEMAIL:info@galluradrive.it\nURL:${window.location.origin}\nADR;TYPE=WORK:;;Olbia;SS;;Italy\nNOTE:Autista privato Gallura - Chrysler 300C Touring\nEND:VCARD`}
                  size={220}
                  fgColor="#0A2463"
                  bgColor="#FFFFFF"
                  level="H"
                  includeMargin={true}
                />
              </div>
              <Button
                data-testid="qr-vcard-download-btn"
                className="brand-bg text-white rounded-full mt-5"
                onClick={() => {
                  const url = document.getElementById("gallura-vcard-qr").toDataURL("image/png");
                  const link = document.createElement("a");
                  link.download = "gallura-drive-contatto-qr.png";
                  link.href = url;
                  link.click();
                }}
              >
                📥 Scarica PNG
              </Button>
            </div>
          </div>
        )}

        {tab === "messages" && (
          <div className="space-y-3">
            {messages.length === 0 && (
              <div className="text-slate-500 bg-white rounded-xl p-6 text-center">
                Nessun messaggio
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className="bg-white rounded-xl p-5 border border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="font-medium brand-text">{m.name}</div>
                  <div className="text-xs text-slate-400">{new Date(m.created_at).toLocaleString("it-IT")}</div>
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  ✉️ {m.email} {m.phone ? `· 📞 ${m.phone}` : ""}
                </div>
                <div className="text-slate-700 mt-3 whitespace-pre-wrap">{m.message}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "reviews" && (
          <div data-testid="admin-reviews-tab" className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600">
                {reviews.length} recensioni totali · <span className="text-emerald-700">{reviews.filter(r => r.published).length} pubblicate</span>
              </div>
              <Button
                data-testid="admin-review-new-btn"
                onClick={() => setReviewDraft({ ...emptyReview })}
                className="brand-bg text-white"
              >
                <Plus className="w-4 h-4 mr-1" /> Nuova recensione
              </Button>
            </div>

            {/* Editor modal */}
            {reviewDraft && (
              <div
                data-testid="admin-review-editor"
                className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4"
                onClick={() => setReviewDraft(null)}
              >
                <div
                  className="bg-white rounded-2xl p-6 w-full max-w-xl space-y-4 max-h-[90vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="font-display text-xl brand-text">
                    {reviewDraft.id ? "Modifica recensione" : "Nuova recensione"}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Autore *</Label>
                      <Input
                        data-testid="review-author-input"
                        value={reviewDraft.author}
                        onChange={(e) => setReviewDraft({ ...reviewDraft, author: e.target.value })}
                        placeholder="Marco R."
                      />
                    </div>
                    <div>
                      <Label>Lingua</Label>
                      <select
                        data-testid="review-lang-select"
                        className="w-full h-10 border border-slate-300 rounded-md px-3"
                        value={reviewDraft.language}
                        onChange={(e) => setReviewDraft({ ...reviewDraft, language: e.target.value })}
                      >
                        <option value="it">🇮🇹 Italiano</option>
                        <option value="en">🇬🇧 English</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label>Voto</Label>
                    <div className="flex gap-1 mt-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={`admin-star-${n}`}
                          type="button"
                          data-testid={`review-star-${n}`}
                          onClick={() => setReviewDraft({ ...reviewDraft, rating: n })}
                          className="transition-transform hover:scale-110"
                          aria-label={`${n} stelle`}
                        >
                          <Star
                            className={`w-7 h-7 ${
                              n <= reviewDraft.rating ? "accent-text fill-current" : "text-slate-300"
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label>Servizio (opzionale)</Label>
                    <Input
                      data-testid="review-service-input"
                      value={reviewDraft.service_type || ""}
                      onChange={(e) => setReviewDraft({ ...reviewDraft, service_type: e.target.value })}
                      placeholder="Transfer aeroporto Olbia → Santa Teresa"
                    />
                  </div>
                  <div>
                    <Label>Testo recensione *</Label>
                    <textarea
                      data-testid="review-text-input"
                      className="w-full border border-slate-300 rounded-md px-3 py-2 min-h-[120px] font-sans text-sm"
                      value={reviewDraft.text}
                      onChange={(e) => setReviewDraft({ ...reviewDraft, text: e.target.value })}
                      placeholder="Puntualissimo, auto elegante, autista professionale..."
                      maxLength={1000}
                    />
                    <div className="text-xs text-slate-400 text-right">{reviewDraft.text?.length || 0}/1000</div>
                  </div>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="review-verified-checkbox"
                        checked={reviewDraft.verified}
                        onChange={(e) => setReviewDraft({ ...reviewDraft, verified: e.target.checked })}
                      />
                      <ShieldCheck className="w-4 h-4 text-emerald-700" /> Verificata
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="review-published-checkbox"
                        checked={reviewDraft.published}
                        onChange={(e) => setReviewDraft({ ...reviewDraft, published: e.target.checked })}
                      />
                      <Eye className="w-4 h-4 brand-text" /> Pubblica subito
                    </label>
                  </div>
                  <div className="flex gap-2 justify-end pt-2 border-t">
                    <Button
                      variant="outline"
                      data-testid="review-cancel-btn"
                      onClick={() => setReviewDraft(null)}
                    >
                      Annulla
                    </Button>
                    <Button
                      data-testid="review-save-btn"
                      onClick={saveReview}
                      className="brand-bg text-white"
                    >
                      {reviewDraft.id ? "Aggiorna" : "Crea"}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Review list */}
            {reviews.length === 0 && (
              <div className="text-slate-500 bg-white rounded-xl p-6 text-center">
                Nessuna recensione. Crea la prima con il pulsante sopra.
              </div>
            )}
            {reviews.map((r) => (
              <div
                key={r.id}
                data-testid={`admin-review-${r.id}`}
                className={`rounded-xl p-5 border grid md:grid-cols-[1fr_auto] gap-4 ${
                  r.published ? "bg-white border-slate-200" : "bg-slate-100 border-slate-300 opacity-80"
                }`}
              >
                <div>
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <span className="font-display text-base brand-text">{r.author}</span>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={`${r.id}-s${n}`}
                          className={`w-4 h-4 ${
                            n <= r.rating ? "accent-text fill-current" : "text-slate-300"
                          }`}
                        />
                      ))}
                    </div>
                    <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 uppercase text-[10px]">
                      {r.language}
                    </Badge>
                    {r.verified && (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                        <ShieldCheck className="w-3 h-3 mr-1" /> Verificata
                      </Badge>
                    )}
                    {!r.published && (
                      <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                        Non pubblicata
                      </Badge>
                    )}
                  </div>
                  {r.service_type && (
                    <div className="text-xs text-slate-500 mb-2">{r.service_type}</div>
                  )}
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">"{r.text}"</p>
                  <div className="text-xs text-slate-400 mt-2">
                    {new Date(r.created_at).toLocaleDateString("it-IT")}
                  </div>
                </div>
                <div className="flex md:flex-col gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`admin-review-edit-${r.id}`}
                    onClick={() => setReviewDraft({ ...r })}
                  >
                    <Edit3 className="w-4 h-4 mr-1" /> Modifica
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`admin-review-toggle-${r.id}`}
                    onClick={() => togglePublish(r)}
                  >
                    {r.published ? (
                      <><EyeOff className="w-4 h-4 mr-1" /> Nascondi</>
                    ) : (
                      <><Eye className="w-4 h-4 mr-1" /> Pubblica</>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`admin-review-delete-${r.id}`}
                    onClick={() => removeReview(r.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
